import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import LocationIcon from 'apps/web_forms/assets/location.svg';
import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useLocation from 'apps/web_forms/hooks/useLocation';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import { addRunningProcess, removeRunningProcess } from 'apps/web_forms/slices/formSlice';
import {
  addLocationValue,
  setCurrentSelectedMultimediaInput,
} from 'apps/web_forms/slices/multimediaSlice';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';

const LocationInputContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  label {
    width: 100%;
  }
  .field-container {
    display: flex;
    width: 100%;
  }
  input {
    width: 100%;
  }
  button {
    background-color: transparent;
    border: none;
    width: auto;
    text-align: right;
    margin-left: 1em;
    font-size: 18px;
    outline: none;
    svg {
      width: 2em;
      height: 2em;
    }
  }
`;

const LocationInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const {
    identifier,
    title,
    hint,
    hidden, 
    auto_resolve: autoResolve,
    validate_msg: validateMsg,
  } = element;
  const { register } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));

  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const [loading, setLoading] = useState(autoResolve);
  const locationValue = useSelector((state) => state.multimedia.locationValues[identifier]);
  
  const dispatch = useDispatch();

  const { setUserAccuracy, changePosition, center, locationStrings } =
    useLocation();

  const [{ isActive, isRequired, isReadOnly, isElementValid, initialAnswer }] =
    useRules(element, formContext);

  const getUserPosition = () => {
    const processName = `${element?.identifier} setUserAccuracy`;
    dispatch(addRunningProcess(processName));
    setUserAccuracy();
    dispatch(removeRunningProcess(processName));
  };

  useEffect(() => {
    if (center && locationStrings) {
      dispatch(
        addLocationValue({
          key: identifier,
          value: {
            center,
            string: locationStrings.string,
            shortString: locationStrings.shortString,
            positionInfo: locationStrings.position,
          },
        })
      );
    }
  }, [center, locationStrings]);

  useEffect(() => {
    if (initialAnswer) {
      const lat = +initialAnswer.match(/(?:lat=)([-|+]?\d+(\.\d+)?)/)[1];
      const lng = +initialAnswer.match(/(?:long=)([-|+]?\d+(\.\d+)?)/)[1];

      if (!Number.isNaN(lat) && !Number.isNaN(lat)) {
        changePosition({ lat, lng });
      }
    }
  }, [initialAnswer]);

  useEffect(() => {
    register(identifier, {
      validate: () => isElementValid || validateMsg || false,
      required: isRequired ? 'This field is required' : false,
    });

    if (autoResolve) {
      getUserPosition();
    }
  }, [identifier, isElementValid, validateMsg, isRequired]);

  useEffect(() => {
    if (locationValue) {
      setLoading(false);
      changeValue(locationValue.string);
    } else {
      changeValue(locationValue);
    }
  }, [locationValue]);

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  const setMapElement = () => {
    if (!isReadOnly && !loading) {
      dispatch(setCurrentSelectedMultimediaInput({ element }));
      removeResourceUpdateHighlight();
    }
  };

  return (
    <div className='react-form-control'>
      <LocationInputContainer>
        <label htmlFor={identifier}>{title}</label>
        <div className='field-container'>
          <input
            name={identifier}
            id={identifier}
            value={locationValue ? locationValue.shortString : ''}
            placeholder={loading ? 'Loading' : ''}
            onClick={setMapElement}
            readOnly
            className={clsx(
              errorMessage !== undefined && 'invalid',
              hasResourceUpdateHighlight && 'updateHighlight',
              isReadOnly && 'read-only'
            )}
          />
          <button
            type='button'
            onClick={setMapElement}
            aria-label='Access Location'
            disabled={isReadOnly || loading}
            data-testid='location-button'
          >
            <LocationIcon />
          </button>
        </div>
        {hint && <p className='element-hint'>{hint}</p>}
        {errorMessage !== undefined && (
          <ErrorMessage id={`${identifier}_errmsg`}>
            {errorMessage}
          </ErrorMessage>
        )}
      </LocationInputContainer>
    </div>
  );
};

LocationInput.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    initialAnswer: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    auto_resolve: PropTypes.bool,
    validate_msg: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(LocationInput);
